import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Home, DollarSign, MapPin, Building2, AlertTriangle, Users, PiggyBank, KeyRound } from "lucide-react";
import { homesRelocationGuide, homesNeighborhood } from "@/lib/api";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`relo-stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-400"><Icon className="h-3.5 w-3.5 text-[#2563EB]" /> {label}</div>
    <div className="mt-1 text-2xl font-black text-[#0F172A]">{value}</div>
  </div>
);

export default function RelocationGuide() {
  const { geoId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [nb, setNb] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0); setD(null); setErr(false); setNb(null);
    homesRelocationGuide(geoId).then(setD).catch(() => setErr(true));
    homesNeighborhood(geoId).then(setNb).catch(() => setNb(null));
  }, [geoId]);

  if (err) return <Center><h1 className="text-3xl font-black text-[#0F172A]">Guide not found</h1><Link to="/homes/relocation" className="mt-3 text-[#2563EB]">← All guides</Link></Center>;
  if (!d) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /><p className="mt-3 text-slate-500">Writing your relocation guide…</p></Center>;

  const m = d.market || {};
  return (
    <div data-testid="relocation-guide">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1100px] px-4 py-12">
          <Link to="/homes/relocation" className="mb-5 inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white"><ArrowLeft className="h-4 w-4" /> All guides</Link>
          <div className="text-xs font-bold uppercase tracking-widest text-[#93B4FF]">Relocation Guide{d.state_name ? ` · ${d.state_name}` : ""}</div>
          <h1 className="mt-2 text-4xl font-black md:text-5xl">Moving to {d.name}</h1>
          {d.intro && <p className="mt-3 max-w-2xl text-lg text-white/80">{d.intro}</p>}
          {d.url_path && (
            <Link to={`/homes/place/${d.geo_id}`} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/20" data-testid="relo-view-market">
              <Home className="h-4 w-4" /> Browse homes & market in {d.name}
            </Link>
          )}
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-4 py-10">
        {d.ai_unavailable && (
          <div className="mb-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="relo-ai-note">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Our AI writer is at today's usage limit, so this is a quick data-backed snapshot. The full narrative guide will generate automatically once capacity resets.</span>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Stat icon={DollarSign} label="Median Home" value={m.median_sale_price ? `$${m.median_sale_price.toLocaleString()}` : "—"} />
          <Stat icon={Building2} label="Median Rent" value={m.median_rent ? `$${m.median_rent.toLocaleString()}/mo` : "—"} />
          <Stat icon={MapPin} label="Listings Tracked" value={m.total ?? "—"} />
        </div>

        {nb && <NeighborhoodBlock nb={nb} />}

        <div className="mt-8 space-y-7" data-testid="relo-sections">
          {(d.sections || []).map((s, i) => (
            <div key={i}>
              <h2 className="text-2xl font-black text-[#0F172A]">{s.title}</h2>
              <p className="mt-2 leading-relaxed text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>

        {(d.areas || []).length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-black text-[#0F172A]">Explore nearby areas</h2>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="relo-areas">
              {d.areas.slice(0, 12).map((a) => (
                <Link key={a.geo_id} to={`/homes/place/${a.geo_id}`}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0F172A] transition-colors hover:border-[#2563EB] hover:text-[#2563EB]">
                  {a.name}{a.listings ? ` · ${a.listings}` : ""}
                </Link>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-xs text-slate-400">HomesPulse guides use sample market data and community intelligence — verify specifics with local sources before relocating.</p>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;

const money = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");

function NeighborhoodBlock({ nb }) {
  const c = nb.census || {};
  const isEstimate = c.estimate || !nb.census_configured;
  const hudRows = nb.hud?.data?.basicdata;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="relo-neighborhood">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-2xl font-black text-[#0F172A]">Community & demographics</h2>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${isEstimate ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="relo-nb-source">
          {c.source || "U.S. Census"}
        </span>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <NbStat icon={Users} label="Population" value={c.population ? c.population.toLocaleString() : "—"} />
        <NbStat icon={PiggyBank} label="Median household income" value={money(c.median_household_income)} />
        <NbStat icon={Home} label="Median home value" value={money(c.median_home_value)} />
        <NbStat icon={KeyRound} label="Median gross rent" value={c.median_gross_rent ? `${money(c.median_gross_rent)}/mo` : "—"} />
        {typeof c.owner_pct === "number" && <NbStat icon={Home} label="Owner-occupied" value={`${c.owner_pct}%`} />}
      </div>
      {Array.isArray(hudRows) && hudRows.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600" data-testid="relo-nb-hud">
          <span className="font-bold text-[#0F172A]">HUD Fair Market Rents</span> — {hudRows.slice(0, 1).map((r) => (
            <span key={r.year || "fmr"}>Studio {money(r.Efficiency)} · 1BR {money(r["One-Bedroom"])} · 2BR {money(r["Two-Bedroom"])} · 3BR {money(r["Three-Bedroom"])}</span>
          ))}
        </div>
      )}
      {isEstimate && (
        <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800" data-testid="relo-nb-estimate-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Figures shown are HomesPulse estimates from sample data. Connect a free U.S. Census API key (and HUD token) to display live demographics, incomes and fair-market rents.
        </p>
      )}
    </div>
  );
}

const NbStat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-400"><Icon className="h-3.5 w-3.5 text-[#2563EB]" /> {label}</div>
    <div className="mt-1 text-xl font-black text-[#0F172A]">{value}</div>
  </div>
);
